import { format, formatDistanceToNow } from "date-fns";
import React from "react";
import { graphql, useFragment } from "react-relay";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { Card, CardContent, CardHeader } from "src/components/ui/card";

import { AuditLogsPreview_auditLogs$key } from "./__generated__/AuditLogsPreview_auditLogs.graphql";

const auditLogsPreviewFragment = graphql`
  fragment AuditLogsPreview_auditLogs on GraphQLAuditLogConnection {
    edges {
      node {
        id
        category
        entityType
        timestamp
        value
        user {
          id
          firstName
          lastName
          email
        }
        name
        action
      }
    }
  }
`;

interface AuditLogsPreviewProps {
  auditLogs: AuditLogsPreview_auditLogs$key;
  isSearching: boolean;
  onClearFilters: () => void;
}

export default function AuditLogsPreview({
  auditLogs,
  isSearching,
  onClearFilters
}: AuditLogsPreviewProps) {
  const data = useFragment(auditLogsPreviewFragment, auditLogs);
  const logs = data.edges?.map(edge => edge.node) || [];

  const getCategoryColor = (category: string, entityType: string) => {
    switch (category) {
      case "KNOWLEDGE_CATALOG":
        return "bg-blue-500/20 text-blue-300 border-blue-500/30";
      case "DIGEST":
        return "bg-green-500/20 text-green-300 border-green-500/30";
      case "ALERT":
        switch(entityType) {
          case "ALERT":
            return "bg-purple-500/20 text-purple-300 border-purple-500/30";
          case "JOB":
            return "bg-red-500/20 text-red-300 border-red-500/30";
        }
    }
    return "bg-muted text-muted-foreground border-neutral-500/30";
  };

  const formatSubCategory = (category: string, entityType: string) => {
    switch (category) {
      case "KNOWLEDGE_CATALOG":
        return "Knowledge Catalog";
      case "DIGEST":
        return "Digest";
      case "ALERT":
        switch(entityType) {
          case "ALERT":
            return "Alert";
          case "JOB":
            return "Runbook";
        }
        break;
    }
    return entityType;
  };

  const formatLogValue = (value: any) => {
    if (!value) return "null";
    if (typeof value === "string") return value;
    return JSON.stringify(value, null, 2);
  };

  return (
    <div className="space-y-4">
      {logs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground text-center">
              No audit logs found.
            </p>
            <Button
              onClick={onClearFilters}
              variant="secondary"
              size="sm"
              className="mt-4"
            >
              Clear filters
            </Button>
          </CardContent>
        </Card>
      ) : (
        logs.map((log) => (
          <Card key={log.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Badge
                      variant="outline"
                      className={getCategoryColor(log.category, log.entityType)}
                    >
                      {formatSubCategory(log.category, log.entityType)}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {log.name}
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(log.timestamp), "yyyy-MM-dd HH:mm:ss.SSS z")} ({formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })})
                    {log.user && (
                    <p className="text-sm text-muted-foreground">
                      by {log.user.firstName} {log.user.lastName} ({log.user.email})
                    </p>
                  )}
                  </span>
                </div>
              </div>
            </CardHeader>
            { log.action && (
              <CardContent>
              <pre className="text-sm text-muted-foreground whitespace-pre-wrap font-mono bg-card/50 p-3 rounded-md overflow-x-auto">
                {formatLogValue(log.action)}
              </pre>
            </CardContent>
              )}
            { log.value && (
              <CardContent>
              <pre className="text-sm text-muted-foreground whitespace-pre-wrap font-mono bg-card/50 p-3 rounded-md overflow-x-auto">
                {formatLogValue(log.value)}
              </pre>
            </CardContent>
            )}
          </Card>
        ))
      )}
      {isSearching && (
      <div className="flex items-center justify-center py-4">
        <div className="text-sm text-muted-foreground">Searching...</div>
      </div>
      )}
    </div>

  );
}
